import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { X, UserRound, Plus, Search as SearchIcon } from "lucide-react";
import { Btn, Input, Select } from "@/components/common";
import { api, apiError } from "@/lib/api";

const ETYPES = ["person", "business", "organization", "place", "group", "event"];

// Shared linked-record control used everywhere a contact/record is linked.
// Props:
//  workspaceId, label (purpose, e.g. "Spouse of whom?"), value ({id,name,type}|null),
//  onChange(record|null), allowMe, allowUnset, typeFilter (array|null), excludeId, required, error
export default function RecordPicker({
  workspaceId, label, value, onChange, allowMe = false, allowUnset = true,
  typeFilter = null, excludeId = null, required = false, error = "", testid = "record-picker",
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("search"); // search | create | me-setup
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [draft, setDraft] = useState({ name: "", type: typeFilter?.[0] || "person" });
  const [meSuggest, setMeSuggest] = useState("");
  const [busy, setBusy] = useState(false);

  const search = useCallback(async () => {
    if (!open || mode !== "search") return;
    const { data } = await api.get(`/entities?workspace_id=${workspaceId}&search=${encodeURIComponent(q)}&limit=20`);
    let items = data.items.filter((e) => e.id !== excludeId);
    if (typeFilter) items = items.filter((e) => typeFilter.includes(e.type));
    setResults(items);
  }, [open, mode, q, workspaceId, excludeId, typeFilter]);
  useEffect(() => { search(); }, [search]);

  const pick = (r) => { onChange({ id: r.id, name: r.name, type: r.type }); setOpen(false); setQ(""); };

  const createInline = async () => {
    if (!draft.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const { data } = await api.post(`/entities?workspace_id=${workspaceId}`, {
        name: draft.name.trim(), type: draft.type, relationship_stage: "candidate", verification_status: "unverified",
      });
      toast.success(`Created "${data.name}"`);
      pick(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const chooseMe = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/me/contact?workspace_id=${workspaceId}`);
      if (data.needs_setup) { setMeSuggest(data.suggested_name || ""); setMode("me-setup"); setOpen(true); }
      else pick(data.contact);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmMe = async (payload) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/me/contact?workspace_id=${workspaceId}`, payload);
      toast.success("“Me” is now linked to your contact");
      pick(data.contact);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={testid}>
      {label && <div className="si-label mb-1.5">{label}</div>}

      {value ? (
        <div className="flex items-center justify-between px-3 py-2" style={{ background: "var(--si-bg)", border: "1px solid var(--si-border)", borderRadius: 2 }}>
          <span className="text-sm truncate" style={{ color: "var(--si-text)" }} data-testid={`${testid}-selected`}>{value.name}{value.type && <span className="si-label ml-2">{value.type}</span>}</span>
          <button type="button" onClick={() => { onChange(null); setOpen(false); }} data-testid={`${testid}-clear`} style={{ color: "var(--si-text-2)" }} title="Change / clear"><X size={15} /></button>
        </div>
      ) : (
        <div>
          {!open ? (
            <div className="flex flex-wrap gap-2">
              <Btn variant="secondary" type="button" onClick={() => { setMode("search"); setOpen(true); }} data-testid={`${testid}-open`}><SearchIcon size={14} /> Select…</Btn>
              {allowMe && <Btn variant="ghost" type="button" onClick={chooseMe} disabled={busy} data-testid={`${testid}-me`}><UserRound size={14} /> Me</Btn>}
              {allowUnset && <span className="si-label self-center">optional — leave unset</span>}
            </div>
          ) : (
            <div className="p-3" style={{ background: "var(--si-bg)", border: "1px solid var(--si-border)", borderRadius: 2 }}>
              <div className="flex gap-1 mb-3" style={{ width: "fit-content", border: "1px solid var(--si-border)", borderRadius: 2 }}>
                {[["search", "Select existing"], ["create", "Create new"]].map(([k, l]) => (
                  <button key={k} type="button" data-testid={`${testid}-tab-${k}`} onClick={() => setMode(k)} className="px-3 py-1.5 text-sm"
                    style={{ background: mode === k ? "var(--si-surface-hover)" : "transparent", color: mode === k ? "var(--si-text)" : "var(--si-text-2)" }}>{l}</button>
                ))}
                <button type="button" onClick={() => setOpen(false)} className="px-3 py-1.5 text-sm" style={{ color: "var(--si-text-2)" }}>Cancel</button>
              </div>

              {mode === "search" && (
                <div>
                  <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search existing contacts…" data-testid={`${testid}-search`} />
                  <div className="mt-2 max-h-40 overflow-y-auto" style={{ border: "1px solid var(--si-border)", borderRadius: 2 }}>
                    {results.map((r) => (
                      <button key={r.id} type="button" data-testid={`${testid}-opt-${r.id}`} onClick={() => pick(r)} className="w-full text-left px-3 py-2 text-sm" style={{ color: "var(--si-text)", borderBottom: "1px solid var(--si-border)" }}>
                        {r.name} <span className="si-label ml-2">{r.type}</span>
                      </button>
                    ))}
                    {results.length === 0 && <div className="text-sm py-3 text-center" style={{ color: "var(--si-text-2)" }}>No matches — use “Create new”.</div>}
                  </div>
                </div>
              )}

              {mode === "create" && (
                <div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input autoFocus value={draft.name} onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))} placeholder="Name (minimum needed)" data-testid={`${testid}-new-name`} />
                    {!typeFilter && <Select value={draft.type} onChange={(e) => setDraft((p) => ({ ...p, type: e.target.value }))}>{ETYPES.map((t) => <option key={t} value={t}>{t}</option>)}</Select>}
                  </div>
                  {draft.name.trim() && results.filter((r) => r.name.toLowerCase().includes(draft.name.trim().toLowerCase())).length > 0 && (
                    <div className="mt-2 text-xs p-2" style={{ background: "var(--si-surface)", border: "1px solid var(--si-warning)", borderRadius: 2, color: "var(--si-text-2)" }} data-testid={`${testid}-dupe-hint`}>
                      Possible existing match — pick instead of creating a duplicate:
                      {results.filter((r) => r.name.toLowerCase().includes(draft.name.trim().toLowerCase())).slice(0, 3).map((r) => (
                        <button key={r.id} type="button" onClick={() => pick(r)} className="block underline mt-1" style={{ color: "var(--si-teal-light)" }}>{r.name} ({r.type})</button>
                      ))}
                    </div>
                  )}
                  <div className="text-xs mt-2" style={{ color: "var(--si-text-2)" }}>Creates one new {typeFilter ? typeFilter[0] : draft.type} record, then selects it here. Nothing is linked until you save this form.</div>
                  <Btn variant="primary" type="button" className="mt-2" onClick={createInline} disabled={busy} data-testid={`${testid}-create`}><Plus size={14} /> {busy ? "Creating…" : "Create & select"}</Btn>
                </div>
              )}

              {mode === "me-setup" && (
                <div data-testid={`${testid}-me-setup`}>
                  <div className="text-sm mb-2" style={{ color: "var(--si-text)" }}>Set up “Me” for this workspace — one time.</div>
                  <div className="text-xs mb-3" style={{ color: "var(--si-text-2)" }}>Pick the existing contact that is you, or create one. It’s linked to your account and reused everywhere. We never match by name automatically.</div>
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for your own contact…" data-testid={`${testid}-me-search`} />
                  <div className="mt-2 max-h-32 overflow-y-auto" style={{ border: "1px solid var(--si-border)", borderRadius: 2 }}>
                    {results.filter((r) => r.type === "person").map((r) => (
                      <button key={r.id} type="button" onClick={() => confirmMe({ entity_id: r.id })} className="w-full text-left px-3 py-2 text-sm" style={{ color: "var(--si-text)", borderBottom: "1px solid var(--si-border)" }} data-testid={`${testid}-me-opt-${r.id}`}>
                        {r.name} <span className="si-label ml-2">this is me</span>
                      </button>
                    ))}
                  </div>
                  <Btn variant="primary" type="button" className="mt-2" onClick={() => confirmMe({ create: true, name: meSuggest })} disabled={busy} data-testid={`${testid}-me-create`}><Plus size={14} /> Create “{meSuggest}” as my contact</Btn>
                </div>
              )}
            </div>
          )}
          {required && error && <div className="text-xs mt-1" style={{ color: "var(--si-danger)" }} data-testid={`${testid}-error`}>{error}</div>}
        </div>
      )}
    </div>
  );
}
